import api from '@/modules/api'
import { AxiosResponse } from 'axios'
import { IApiResponse } from '@/common/models/interface'
import { EnumPaymentStatus, EnumTutorialStatus } from '@/common/models/enum'
import { MemberParam, ResetPasswordEmailParam, ResetPasswordParam } from '@/services/models'
import { InUseSubscriptionsRsp, SubscriptionProfileRsp } from './models/Member'
import { SubscriptionRsp } from '@/services/models/Member'

export default class MemberService {
  /**
   * Register
   *
   * @param {MemberParam} payload
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async register(payload: MemberParam): Promise<any> {
    const res: AxiosResponse<any> = await api.post('Member', payload)
    return res.data
  }

  /**
   * Check email exists
   *
   * @param {string} email
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async emailExists(email: string): Promise<any> {
    try {
      const res: AxiosResponse<any> = await api.get(`Member/Exists?email=${email}`)
      return res.data
    } catch (e: any) {
      if (e.response.status === 404) {
        return {
          data: {
            exists: false
          }
        }
      }
    }
  }

  /**
   * Confirm email
   *
   * @param {string} memberId
   * @param {string} confirmationToken
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async confirmEmail(memberId: string, confirmationToken: string): Promise<any> {
    const res: AxiosResponse<any> = await api.get(
      `Member/${memberId}/ConfirmEmail/${confirmationToken}`
    )
    return res.data
  }

  /**
   * Send reset password email
   *
   * @param {ResetPasswordEmailParam} payload
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async sendResetPasswordEmail(payload: ResetPasswordEmailParam): Promise<any> {
    const res: AxiosResponse<any> = await api.post('Member/ResetPasswordEmail', payload)
    return res.data
  }

  /**
   * Get reset password member info
   *
   * @param {string} memberId
   * @param {string} resetPasswordToken
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async getResetPasswordMemberInfo(
    memberId: string,
    resetPasswordToken: string
  ): Promise<any> {
    const res: AxiosResponse<any> = await api.get(
      `Member/${memberId}/ResetPassword/MemberInfo/${resetPasswordToken}`
    )
    return res.data
  }

  /**
   * reset password
   *
   * @param {ResetPasswordParam} payload
   * @returns {Promise<any>}
   * @memberof MemberService
   */
  public async resetPassword(payload: ResetPasswordParam): Promise<any> {
    const res: AxiosResponse<any> = await api.post('Member/ResetPassword', payload)
    return res.data
  }

  /**
   * Get member subscription information
   *
   * @returns {Promise<IApiResponse<InUseSubscriptionsRsp>>}
   * @memberof MemberService
   */
  public async getInUseSubscriptions(): Promise<IApiResponse<InUseSubscriptionsRsp[]>> {
    const res: AxiosResponse<any> = await api.get('Member/InUseSubscriptions')
    return res.data
  }

  /**
   * get member profile, use in frontend refresh
   *
   * @returns member profile
   */
  public async getMemberProfile(): Promise<IApiResponse<any>> {
    const res: AxiosResponse<any> = await api.get('Member/Profile')
    return res.data
  }

  /*
   * get member subscription record
   * */
  public async getSubscriptions(params: {
    status?: EnumPaymentStatus
    page?: number
    limit?: number
    orderBy?: string
    ascending?: number
  }): Promise<IApiResponse<SubscriptionRsp[]>> {
    // const res: AxiosResponse<any> = await api.get('Member/Subscriptions', {params})
    return api.get('Member/Subscriptions', { params })
  }

  /**
   * set member tutorial status
   * @param status
   */
  public async setTutorialStatus(status: EnumTutorialStatus): Promise<void> {
    await api.post(`Member/SetTutorialStatus/${status}`)
  }

  /**
   * member consent eula
   */
  public async consentEula(): Promise<void> {
    await api.post('Member/ConsentEula')
  }

  /**
   * sent verification email
   */
  public async sendVerificationEmail(accessToken: string | null = null): Promise<void> {
    if (accessToken != null) {
      const config = {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      }
      await api.post('Member/VerificationEmail', {}, config)
    } else {
      await api.post('Member/VerificationEmail', {})
    }
  }

  public async getProfile(): Promise<IApiResponse<SubscriptionProfileRsp>> {
    const res: AxiosResponse = await api.get('Member/SubscriptionProfile')
    return res.data
  }
}
